import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import type { WeekProgress } from "./weekWindow.js";

// The EXTERNAL metre (measurement-contract §17b.2). Until now the only two
// references the app owned were the athlete's own habit (a trimmed mean of his
// 90 days, `muscleBaselines.ts`) and a number he typed himself (`meTargets.ts`,
// client-authored). Both answer "am I doing what I usually do / said I would" —
// neither can answer "I was doing DOUBLE what I needed", because a system whose
// only reference is you-of-yesterday cannot tell you yesterday was wrong.
//
// This module is the third reference: a band that does NOT come from his data.
// It lives on the hard-set axis (sets per muscle per week), the axis that
// already exists (`hardSetsByMuscle`, `TargetItem.metric: "hard-sets"`).
//
// ⛔ NEVER put this on the tonnage axis. MEV/MAV/MRV is a COUNT OF SETS; the
// existing baseline surveillance is KILOGRAMS (`volumeByMuscle.ts`). Two
// different quantities, never the same axis (measurement-contract §16.3).
//
// ⛔ Weights only. A landmark band is defined for resistance training; a run
// does not score low for calves on this scale, it has no value on it at all
// (§16.11). Cardio never reaches here — it has its own map.
//
// ⛔ The band answers "am I in the right zone", NEVER "you must stop"
// (§16.2: state the MEASURE, not the instruction).

/** Where a landmark band comes from. The band is the first number Metria shows
 *  that is NOT derived from the athlete's own data, so it may never appear
 *  without its provenance attached — same discipline as the calorie (i) pill
 *  (`energySources.ts`), but landmark-shaped, so it stays in this thin layer
 *  instead of reshaping the metric-agnostic registry (`metricSources.ts`).
 *
 *  Holds i18n KEYS, never human strings: a new source cannot ship an
 *  English-only label. */
export interface VolumeLandmarkSource {
  id: string;
  /** i18n key for the source's name. */
  labelKey: string;
  /** i18n key for the one-line "where this number comes from" caveat. */
  citationKey: string;
}

/** Renaissance Periodization's published volume landmarks (Israetel et al.).
 *  Practice reference points, NOT settled physiology: the dose-response of sets
 *  to hypertrophy with diminishing returns has support, but the per-muscle
 *  values — and the MRV ceiling above all — carry enormous individual variance
 *  and thin direct evidence (§17b.2). Declared, cited, and meant to be narrowed
 *  by the athlete's own data; ⛔ never presented as a verdict. */
export const RP_LANDMARK_SOURCE: VolumeLandmarkSource = {
  id: "rp:israetel",
  labelKey: "progress.volumeLandmark.source.rp",
  citationKey: "progress.volumeLandmark.citation.rp",
};

/** A weekly band in HARD SETS for one muscle.
 *  - `mev` minimum effective volume: below it, the week is unlikely to train.
 *  - `mav` [low, high] maximum adaptive volume: the productive range.
 *  - `mrv` maximum recoverable volume: above it, recovery is the limit.
 *  All in sets/week, on the same fractional-credit scale `hardSetsByMuscle`
 *  produces (a synergist earns a fraction, so totals are not integers). */
export interface VolumeLandmarkBand {
  mev: number;
  mav: [number, number];
  mrv: number;
  source: VolumeLandmarkSource;
}

// ⛔ PARTIAL ON PURPOSE, and the gaps are the honest part. RP publishes
// landmarks for the muscles it trains as units; our catalog has 19 slugs and
// three of them (adductors, abductors, obliques) have NO published band —
// obliques are folded into "abs" in the source, and the hip ab/adductors are
// not published at all. A muscle with no row here reads as UNKNOWN, a third
// state: it is never given a borrowed number and never silently scored against
// a neighbour's band. An invented band would be worse than no band, because it
// would look exactly as authoritative as a real one.
const RP_BANDS: Partial<Record<CatalogMuscleSlug, Omit<VolumeLandmarkBand, "source">>> = {
  // Lower body
  quadriceps: { mev: 8, mav: [12, 18], mrv: 20 },
  hamstrings: { mev: 4, mav: [10, 16], mrv: 20 },
  glutes: { mev: 0, mav: [4, 12], mrv: 16 },
  calves: { mev: 8, mav: [12, 16], mrv: 20 },
  // Trunk
  core: { mev: 0, mav: [16, 20], mrv: 25 },
  erectors: { mev: 6, mav: [8, 12], mrv: 16 },
  // Upper-body push
  pectoralis: { mev: 8, mav: [12, 20], mrv: 22 },
  "anterior-delts": { mev: 0, mav: [6, 12], mrv: 12 },
  "lateral-delts": { mev: 8, mav: [16, 22], mrv: 26 },
  triceps: { mev: 6, mav: [10, 14], mrv: 18 },
  // Upper-body pull
  lats: { mev: 10, mav: [14, 22], mrv: 25 },
  rhomboids: { mev: 10, mav: [14, 22], mrv: 25 },
  "rear-delts": { mev: 6, mav: [12, 20], mrv: 26 },
  traps: { mev: 0, mav: [12, 20], mrv: 26 },
  biceps: { mev: 8, mav: [14, 20], mrv: 26 },
  // Distal
  forearms: { mev: 2, mav: [8, 12], mrv: 16 },
};

/** The band for a muscle, or `undefined` when the reference publishes none.
 *  ⛔ Callers must render the absence as an absence — no fallback band. */
export const landmarkBandFor = (
  slug: CatalogMuscleSlug,
): VolumeLandmarkBand | undefined => {
  const b = RP_BANDS[slug];
  return b ? { ...b, source: RP_LANDMARK_SOURCE } : undefined;
};

/** Which muscles the reference covers — so a caller can say "3 of your 19
 *  muscles have no published band" instead of quietly showing 16. */
export const musclesWithLandmarkBand = (): CatalogMuscleSlug[] =>
  Object.keys(RP_BANDS) as CatalogMuscleSlug[];

/** Where a week's volume sits against the band. Four zones, because the band
 *  has three boundaries — collapsing them to the existing 3-state `TargetZone`
 *  (under / in-band / over) would lose the distinction between "building" and
 *  "at the productive plateau", which is the whole point of the metre. */
export type VolumeLandmarkZone =
  | "below-mev"
  | "mev-mav"
  | "mav-mrv"
  | "above-mrv";

/** One muscle's weekly reading against the external band.
 *  `zone` is undefined exactly when `band` is: unknown is not a zone. */
export interface MuscleLandmarkReading {
  muscle: CatalogMuscleSlug;
  /** Hard sets this week, fractional credit included. */
  weeklySets: number;
  band?: VolumeLandmarkBand;
  zone?: VolumeLandmarkZone;
}

/** Score one muscle's weekly hard sets against its band.
 *  Boundaries are inclusive at the bottom of each zone: exactly MEV is IN the
 *  MEV-MAV zone (the volume is effective), exactly MRV is still within it (the
 *  ceiling is reached, not exceeded). */
export const landmarkZoneFor = (
  weeklySets: number,
  band: VolumeLandmarkBand,
): VolumeLandmarkZone => {
  if (weeklySets < band.mev) return "below-mev";
  if (weeklySets < band.mav[0]) return "mev-mav";
  if (weeklySets <= band.mrv) return "mav-mrv";
  return "above-mrv";
};

/** The sets the band asks for by the end of today (PG-64 fetta 2, the
 *  compact coverage): MEV pro rata on the days of the calendar week gone by,
 *  today included. On the reset day it is one seventh of MEV, on the last
 *  day all of it. The pace the week is read against, so that a Tuesday is
 *  not scored against a whole week (§8bis.1). Sets, fractional. */
export const expectedSetsToDate = (
  band: Pick<VolumeLandmarkBand, "mev">,
  week: Pick<WeekProgress, "dayIndex">,
): number => (band.mev * (Math.min(6, Math.max(0, week.dayIndex)) + 1)) / 7;

/** Read a whole week against the external band.
 *
 *  Input is the map `hardSetsByMuscle` already produces (muscle slug → weekly
 *  hard sets, fractional). Output covers EVERY muscle the reference knows,
 *  including the ones absent from the input — a muscle trained zero times this
 *  week is the signal, not a missing row, and dropping it would hide exactly
 *  the "muscles not yet worked" the proposal has to fill.
 *
 *  Muscles the input carries but the reference does not cover are returned too,
 *  with no band and no zone, so a caller can show them as unmeasured rather
 *  than pretend they don't exist. */
export const weeklyLandmarkReadings = (
  hardSetsByMuscle: Readonly<Record<string, number>>,
): MuscleLandmarkReading[] => {
  const seen = new Set<string>();
  const out: MuscleLandmarkReading[] = [];
  for (const muscle of musclesWithLandmarkBand()) {
    seen.add(muscle);
    const band = landmarkBandFor(muscle);
    const weeklySets = hardSetsByMuscle[muscle] ?? 0;
    out.push({
      muscle,
      weeklySets,
      band,
      zone: band ? landmarkZoneFor(weeklySets, band) : undefined,
    });
  }
  for (const [muscle, weeklySets] of Object.entries(hardSetsByMuscle)) {
    if (seen.has(muscle)) continue;
    out.push({
      muscle: muscle as CatalogMuscleSlug,
      weeklySets: weeklySets ?? 0,
    });
  }
  return out;
};

/** The muscles asking for work: below MEV, worst gap first.
 *  This is the input to the proposal half ("which exercises × sets × reps for
 *  the muscles not yet worked") — a muscle never trained this week sits here
 *  with `weeklySets: 0` and the full MEV as its gap.
 *  ⛔ Muscles with no published band are NOT listed: we cannot claim a gap
 *  against a band that does not exist. */
export const musclesBelowMev = (
  readings: ReadonlyArray<MuscleLandmarkReading>,
): Array<{ muscle: CatalogMuscleSlug; weeklySets: number; gap: number }> =>
  readings
    .filter((r) => r.band !== undefined && r.zone === "below-mev")
    .map((r) => ({
      muscle: r.muscle,
      weeklySets: r.weeklySets,
      gap: (r.band as VolumeLandmarkBand).mev - r.weeklySets,
    }))
    .sort((a, b) => b.gap - a.gap);
